import { useTranslations } from 'next-intl';
import { useRef } from 'react';
import { Swiper, SwiperSlide } from 'swiper/react';

import { ChevronIcon } from '@/component/Icons/ChevronIcon';
import { SliderNewsCard } from './SliderNewsCard';
import { SliderListCard } from './SliderListCard';
import { SliderReviewsCard } from './SliderReviewsCard';
import { SliderCasesCard } from './SliderCasesCard';

import 'swiper/css';
import { Link } from '@/i18n/routing';

export const SliderList = (props) => (
  <Slider {...props} CardComponent={SliderListCard} />
);

export const SliderNews = (props) => (
  <Slider {...props} link="/blogs" CardComponent={SliderNewsCard} />
);

export const SliderReviews = (props) => (
  <Slider
    {...props}
    link="/reviews"
    target="_blank"
    CardComponent={SliderReviewsCard}
  />
);

export const SliderBlogs = (props) => (
  <Slider {...props} link="/blogs" CardComponent={SliderCasesCard} />
);

const Slider = ({
  titleKey,
  linkKey,
  linkText,
  subtitleKey,
  items,
  readMore,
  bgGray,
  title,
  titleSmall,
  link,
  target,
  classes,
  classesWrap,
  classesSlider,
  classesHeader,
  classesFooter,
  CardComponent,
  widthLarge,
  dataTestId,
  onLoadMore,
  loading,
}) => {
  const swiperRef = useRef();

  const t = useTranslations();

  const buttonResponsiveClass = getButtonResponsive(widthLarge, items.length);

  return (
    <section
      className={`tab-slider 3xl:rounded-10 3xl:container 3xl:px-0 3xl:overflow-hidden 3xl:mx-auto ${classesWrap}`}
      data-testid={dataTestId}
    >
      <div
        className={`container max-lg:pt-8 lg:pt-[46px] ${classesHeader} ${
          bgGray ? 'max-3xl:!bg-slate-50' : null
        }`}
      >
        <div
          className={`content-animated flex justify-between sm:items-end ${
            titleSmall ? 'max-sm:gap-4' : 'max-sm:flex-col max-sm:gap-4'
          }`}
        >
          <div className="space-y-[10px]">
            <h2
              className={`text-primary-800 max-sm:text-2xl text-[40px] leading-[normal] ${
                titleSmall
                  ? 'max-sm:text-base max-sm:leading-tight max-sm:tracking-[0.3px] sm:!text-2xl font-normal'
                  : 'font-semibold'
              }`}
              data-testid={dataTestId ? 'slider-title' : null}
            >
              {title || t(titleKey)}
            </h2>
            {subtitleKey && (
              <p
                className="text-slate-500 max-sm:text-base text-lg max-w-[520px]"
                dangerouslySetInnerHTML={{ __html: t.raw(subtitleKey) }}
              />
            )}
          </div>

          <div
            className={`flex gap-x-[18px] max-sm:justify-end ${buttonResponsiveClass}`}
          >
            <button
              className="btn btn-second btn-sm btn-icon h-min rotate-180"
              aria-label="gallery prev button"
              onClick={() => {
                swiperRef.current?.swiper.slidePrev();
              }}
            >
              <ChevronIcon />
            </button>
            <button
              className="btn btn-second btn-sm btn-icon  h-min"
              aria-label="gallery next button"
              onClick={() => {
                swiperRef.current?.swiper.slideNext();
              }}
            >
              <ChevronIcon />
            </button>
          </div>
        </div>
      </div>

      <div
        className={`container swiper-wrap-box !px-0 ${
          bgGray ? 'swiper-wrap-box-gray' : 'swiper-wrap-box-white'
        } ${classes} ${bgGray ? 'max-3xl:!bg-slate-50' : null}`}
      >
        <Swiper
          className={`content-animated !py-8 ${classesSlider} ${
            bgGray ? 'max-3xl:bg-slate-50' : null
          } ${titleSmall ? '!pt-[18px]' : null}`}
          slidesPerView="auto"
          ref={swiperRef}
          onProgress={(_, progress) => {
            if (!!onLoadMore && progress >= 0.7 && !loading) {
              onLoadMore();
            }
          }}
        >
          {items.map((item, index) =>
            CardComponent ? (
              <SwiperSlide
                key={`${item.slug}-${index}`}
                className={`slide-box max-sm:mr-[18px] sm:last:mr-8 lg:last:mr-16 mr-8 !h-auto max-sm:max-w-[260px] sm:max-w-[310px] ${
                  widthLarge ? 'max-sm:max-w-[298px] sm:max-w-[520px]' : null
                }`}
              >
                <CardComponent
                  item={item}
                  bgGray={bgGray}
                  dataTestId={dataTestId ? 'slider-card-component' : undefined}
                />
              </SwiperSlide>
            ) : (
              item.slug
            )
          )}
        </Swiper>
      </div>
      {readMore && (
        <div
          className={`container max-lg:pb-8 lg:pb-[46px] ${classesFooter} ${
            bgGray ? 'max-3xl:!bg-slate-50' : null
          }`}
        >
          <Link
            className="content-animated btn btn-second"
            href={link}
            target={target}
          >
            {linkText || t(linkKey || 'slider.news.link')}
          </Link>
        </div>
      )}
    </section>
  );
};

const getButtonResponsive = (widthLarge, length) =>
  (widthLarge
    ? {
        1: 'hidden',
        2: 'min-[1214px]:hidden',
        3: 'min-[1752px]:hidden',
      }
    : {
        1: 'hidden',
        2: 'min-[738px]:hidden',
        3: 'min-[1136px]:hidden',
        4: 'min-[1464px]:hidden',
        5: 'min-[1806px]:hidden',
      })[length];
